<div
  [class.quick-access-expanded]="quickAccessAvailable() && quickAccessExpanded()"
  class="flex w-full min-w-0 flex-col gap-2 pb-2"
>
  @if (showTaskData()) {
    <fieldset class="task-details-group">
      <legend class="task-details-group-label">{{ 'task-filters.sections.taskData' | translate }}</legend>
      <div class="task-details-status-row">
        @if (displayedVisibility().status) {
          <proffeo-task-status-select
            (statusSelected)="statusSelected.emit($event)"
            [mode]="TaskStatusSelectEnum.DOTS"
            [readonly]="false"
            [taskStatus]="task().status"
            class="task-details-status-select"
          />
        }
        @if (displayedVisibility().progress) {
          <proffeo-ui-slider
            (valueChange)="progressSelected.emit($event)"
            [displayWith]="formatPercent"
            [value]="task().percentageOfProgress ?? 0"
            class="task-details-progress w-[4.9rem]"
          />
        }
        @if (displayedVisibility().priority) {
          <proffeo-task-priority-select
            (prioritySelected)="prioritySelected.emit($event)"
            [readonly]="false"
            [small]="false"
            [taskPriority]="task().priority"
          />
        }
        @if (quickAccessAvailable()) {
          <button
            (click)="quickAccessExpanded.set(!quickAccessExpanded())"
            [attr.aria-expanded]="quickAccessExpanded()"
            [attr.aria-label]="
              (quickAccessExpanded() ? 'task-details-metadata-toggle-collapse' : 'task-details-metadata-toggle-expand')
                | translate
            "
            type="button"
            mat-icon-button
            class="task-details-quick-access-toggle ml-auto self-center shrink-0"
          >
            <mat-icon [fontIcon]="quickAccessExpanded() ? 'expand_less' : 'expand_more'" />
          </button>
        }
      </div>
    </fieldset>
  }

  @if (displayedVisibility().location || displayedVisibility().industry) {
    <fieldset class="task-details-group">
      <legend class="task-details-group-label">{{ 'task-list-group-location' | translate }}</legend>
      <div class="task-details-place-row">
        @if (displayedVisibility().industry) {
          <div class="task-details-field">
            <mat-icon
              fontIcon="business_center"
              class="task-details-field-icon icon-md"
            />
            <proffeo-task-industry-select
              (industryClicked)="industryRequested.emit()"
              [showIcon]="false"
              [isReadonly]="false"
              [projectIndustry]="task().projectIndustry()"
              class="min-w-0"
            />
          </div>
        }
        @if (displayedVisibility().location) {
          <div class="task-details-field">
            <mat-icon
              fontIcon="location_on"
              class="task-details-field-icon icon-md"
            />
            <proffeo-location-select
              (locationClicked)="locationRequested.emit()"
              [showIcon]="false"
              [small]="false"
              [location]="task().location()"
              class="min-w-0"
            />
          </div>
        }
      </div>
    </fieldset>
  }

  @if (displayedVisibility().totalHours) {
    <fieldset class="task-details-group">
      <legend class="task-details-group-label">
        {{ 'project-tasks.list.column-headers.total-hours' | translate }}
      </legend>
      <div class="task-details-field">
        <button
          (click)="openTimeReportRequested.emit()"
          type="button"
          class="task-details-field-action"
        >
          <mat-icon
            fontIcon="play_circle"
            class="icon-md icon-success"
          />
        </button>
        <proffeo-task-timer
          (click)="timeReportsTabRequested.emit()"
          [estimation]="$safeNavigationMigration(task().estimation?.time)"
          [mode]="taskTimerMode.readonly"
          [summaryTime]="$safeNavigationMigration(task().statistics?.summaryTime)"
          class="min-w-0 cursor-pointer"
        />
      </div>
    </fieldset>
  }

  @if (displayedVisibility().startDate || displayedVisibility().endDate) {
    <fieldset class="task-details-group">
      <legend class="task-details-group-label">{{ 'task-filters.sections.dates' | translate }}</legend>
      <div class="task-details-dates">
        @if (displayedVisibility().startDate) {
          <div class="task-details-field">
            <mat-icon
              fontIcon="calendar_today"
              class="task-details-field-icon icon-md"
            />
            <proffeo-ui-date-picker-compact
              (valueChange)="startDateSelected.emit($event)"
              [iconVisible]="false"
              [max]="task().endDate"
              [touchUi]="touchUi()"
              [value]="task().startDate"
              placeholderKey="project-tasks.list.column-headers.start-date"
              class="min-w-0"
            />
          </div>
        }
        @if (displayedVisibility().endDate) {
          <div class="task-details-field">
            <mat-icon
              fontIcon="calendar_today"
              class="task-details-field-icon icon-md"
            />
            <proffeo-ui-date-picker-compact
              (valueChange)="endDateSelected.emit($event)"
              [iconVisible]="false"
              [min]="task().startDate"
              [touchUi]="touchUi()"
              [value]="task().endDate"
              placeholderKey="project-tasks.list.column-headers.end-date"
              class="min-w-0"
            />
          </div>
        }
      </div>
    </fieldset>
  }

  @if (showEstimation()) {
    <fieldset class="task-details-group">
      <legend class="task-details-group-label">{{ 'estimation' | translate }}</legend>
      <proffeo-task-estimation-select
        (estimationSelected)="estimationSelected.emit($event)"
        [estimation]="task().estimation"
        [showCost]="displayedVisibility().estimationCost"
        [showQuantity]="displayedVisibility().estimationQuantity"
        [showTime]="displayedVisibility().estimationTime"
        class="task-details-estimation min-w-0 max-w-full"
      />
    </fieldset>
  }

  @if (displayedVisibility().users) {
    <fieldset class="task-details-group">
      <legend class="task-details-group-label">{{ 'task-filters.sections.users' | translate }}</legend>
      <proffeo-ui-task-assignees
        (changeGroup)="groupRequested.emit()"
        (changeIndustry)="industryRequested.emit()"
        (changeUsers)="participantsRequested.emit()"
        [avatarSize]="AvatarSize.SM"
        [avatarsOverlapping]="false"
        [projectGroupAdmin]="task().projectGroupAdministrator()"
        [projectIndustry]="task().projectIndustry()"
        [taskUsers]="taskUsers()"
      />
    </fieldset>
  }

  @if (displayedVisibility().tags) {
    <fieldset class="task-details-group">
      <legend class="task-details-group-label">
        {{ 'project-tasks.list.column-headers.tags' | translate }}
      </legend>
      <div class="flex min-w-0 flex-wrap items-center gap-1">
        @for (tag of tags(); track tag.id) {
          <proffeo-tag
            (click)="tagsRequested.emit()"
            [tag]="tag"
            class="cursor-pointer"
          />
        }
        @if (tags().length === 0) {
          <proffeo-tag
            (click)="tagsRequested.emit()"
            [tag]="'project-tags.add-project-tag' | translate"
            class="cursor-pointer"
          />
        }
      </div>
    </fieldset>
  }

  @if (displayedVisibility().acknowledgement) {
    <fieldset class="task-details-group">
      <legend class="task-details-group-label">
        {{ 'task-description-acknowledgement-panel.title' | translate }}
      </legend>
      <proffeo-task-acknowledgement-required-panel [task]="task()" />
    </fieldset>
  }
</div>
